import { normalizeStoredDx7Voice, type Dx7Voice } from '@/lib/dx7'
import { type Favourite, readFavourites } from '@/lib/favourites'
import { normalizeFm1Effects } from '@/lib/fm1-effects'
import { isFm1VaEightBitVoice } from '@/lib/fm1-va-eight-bit'
import { readFm1VaRecord } from '@/lib/fm1-va-record'
import { isFm1VaVirtualAnalogVoice } from '@/lib/fm1-va-virtual-analog'
import { type NamedBank, validateNamedBank } from '@/lib/named-bank'
import {
  bankDescriptionLength,
  browserBanks,
  compactWorkspaceBanks,
  isWorkspaceBankId,
  maximumWorkspaceBanks,
  workspaceBankTitleLength,
} from '@/lib/patch-library'

const databaseName = 'fm1-librarian'
const workspaceStoreName = 'library'
const namedBankStoreName = 'named-banks'
export const fxPresetStoreName = 'fx-presets'
const recordKey = 'current'

export type PatchLibraryStorageErrorCode =
  'unavailable' | 'read-failed' | 'incompatible' | 'write-failed' | 'conflict'

/**
 * Which stored workspace a tab last loaded or wrote: its `savedAt`, `''` for a record that has
 * none, or `null` when there was no record. A save refuses to replace any other.
 */
export type StoredWorkspaceRevision = string | null

const currentWorkspaceVersion = 9

function storedRevision(stored: unknown): StoredWorkspaceRevision {
  if (!stored || typeof stored !== 'object') return null
  return 'savedAt' in stored && typeof stored.savedAt === 'string' ? stored.savedAt : ''
}

export class PatchLibraryStorageError extends Error {
  readonly code: PatchLibraryStorageErrorCode
  readonly technicalMessage: string

  constructor(code: PatchLibraryStorageErrorCode, message: string, cause?: unknown) {
    super(cause instanceof Error ? cause.message : message, { cause })
    this.name = 'PatchLibraryStorageError'
    this.code = code
    this.technicalMessage = cause instanceof Error ? `${cause.name}: ${cause.message}` : message
  }
}

export type StoredPatchLibrary = {
  bankDescriptions: Record<string, string>
  bankNames: Record<string, string>
  effects: Record<string, Uint8Array>
  /**
   * The voice bytes of each slot holding an FM-1+VA 8-Bit preset, by slot id, from version 9, kept
   * exactly as read.
   */
  eightBit: Record<string, Uint8Array>
  favourites: Favourite[]
  loadedBanks: string[]
  /** FM-1+VA settings records by slot id, from version 7. Favourites carry their own. */
  records: Record<string, Uint8Array>
  savedAt: string
  version: 9
  /**
   * The voice bytes of each slot holding an FM-1+VA Virtual Analog preset, by slot id, from
   * version 8, kept exactly as read.
   */
  virtualAnalog: Record<string, Uint8Array>
  voices: Record<string, Dx7Voice>
  workspaceBanks: string[]
}

/** Reads the records a workspace stored, keeping only those of slots it still has a patch in. */
function readStoredRecords(value: unknown, filled: (id: string) => boolean) {
  if (!value || typeof value !== 'object') return {}
  return Object.fromEntries(
    Object.entries(value).flatMap(([id, stored]) => {
      const record = filled(id) ? readFm1VaRecord(stored) : undefined
      return record ? [[id, record]] : []
    }),
  )
}

/**
 * Reads the Virtual Analog or 8-Bit voices a workspace stored. They are kept exactly as read, so
 * one that is not seven-bit data of a voice's size cannot be put right, and makes the workspace
 * unreadable rather than being changed. A slot that already holds another sound keeps it.
 */
function readStoredPresetVoices(
  value: unknown,
  isPresetVoice: (stored: unknown) => stored is Uint8Array,
  taken: (id: string) => boolean,
  engine: string,
): Record<string, Uint8Array> {
  if (!value || typeof value !== 'object') return {}
  return Object.fromEntries(
    Object.entries(value).flatMap(([id, stored]) => {
      if (!isPresetVoice(stored)) {
        throw new PatchLibraryStorageError(
          'incompatible',
          `The saved patch library contains an unreadable ${engine} preset.`,
        )
      }
      return taken(id) ? [] : [[id, stored]]
    }),
  )
}

export function asStorageError(
  error: unknown,
  code: PatchLibraryStorageErrorCode,
  message: string,
): PatchLibraryStorageError {
  if (error instanceof PatchLibraryStorageError) return error
  return new PatchLibraryStorageError(code, message, error)
}

export function openDatabase() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    if (!('indexedDB' in globalThis)) {
      reject(new PatchLibraryStorageError('unavailable', 'Browser storage is unavailable.'))
      return
    }

    let request: IDBOpenDBRequest
    try {
      request = indexedDB.open(databaseName, 3)
    } catch (error) {
      reject(
        new PatchLibraryStorageError('unavailable', 'Browser storage could not be opened.', error),
      )
      return
    }
    let finished = false
    const fail = (message: string, cause?: unknown) => {
      if (finished) return
      finished = true
      reject(new PatchLibraryStorageError('unavailable', message, cause))
    }
    request.onerror = () => fail('Browser storage could not be opened.', request.error)
    // The request is still pending while blocked, and reading its `error` then throws.
    request.onblocked = () => fail('Browser storage is blocked by another open tab.')
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(workspaceStoreName)) {
        request.result.createObjectStore(workspaceStoreName)
      }
      if (!request.result.objectStoreNames.contains(namedBankStoreName)) {
        request.result.createObjectStore(namedBankStoreName, { keyPath: 'id' })
      }
      if (!request.result.objectStoreNames.contains(fxPresetStoreName)) {
        request.result.createObjectStore(fxPresetStoreName, { keyPath: 'id' })
      }
    }
    request.onsuccess = () => {
      if (finished) {
        request.result.close()
        return
      }
      finished = true
      resolve(request.result)
    }
  })
}

export async function runTransaction<T>(
  storeName: string,
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore, transaction: IDBTransaction) => IDBRequest<T>,
) {
  const database = await openDatabase()
  return new Promise<T>((resolve, reject) => {
    let transaction: IDBTransaction
    let request: IDBRequest<T>
    try {
      // Strict durability reports a write complete only once it is on disk; browsers without the
      // option ignore it.
      transaction = database.transaction(
        storeName,
        mode,
        mode === 'readwrite' ? { durability: 'strict' } : undefined,
      )
      request = operation(transaction.objectStore(storeName), transaction)
    } catch (error) {
      database.close()
      reject(error instanceof Error ? error : new Error(String(error)))
      return
    }
    let finished = false

    const fail = (error: DOMException | null, fallback: string) => {
      if (finished) return
      finished = true
      database.close()
      reject(error ?? new Error(fallback))
    }

    request.onerror = () => fail(request.error, 'Browser storage operation failed.')
    transaction.oncomplete = () => {
      if (finished) return
      finished = true
      database.close()
      // Read here rather than in `onsuccess`, which an operation may use to chain another request.
      resolve(request.result)
    }
    transaction.onabort = () => fail(transaction.error, 'Browser storage transaction was aborted.')
    transaction.onerror = () => fail(transaction.error, 'Browser storage transaction failed.')
  })
}

function normalizeStoredVoices(voices: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(voices).map(([id, value]) => {
      const voice = normalizeStoredDx7Voice(value)
      if (!voice) {
        throw new PatchLibraryStorageError(
          'incompatible',
          'The saved patch library contains an unreadable voice.',
        )
      }
      return [id, voice]
    }),
  )
}

export async function loadStoredPatchLibrary() {
  let stored:
    | StoredPatchLibrary
    | (Omit<StoredPatchLibrary, 'eightBit' | 'version'> & { version: 8 })
    | (Omit<StoredPatchLibrary, 'eightBit' | 'version' | 'virtualAnalog'> & { version: 7 })
    | (Omit<StoredPatchLibrary, 'eightBit' | 'records' | 'version' | 'virtualAnalog'> & {
        version: 6
      })
    | (Omit<
        StoredPatchLibrary,
        'eightBit' | 'favourites' | 'records' | 'version' | 'virtualAnalog'
      > & {
        version: 5
      })
    | (Omit<
        StoredPatchLibrary,
        'bankDescriptions' | 'eightBit' | 'favourites' | 'records' | 'version' | 'virtualAnalog'
      > & {
        version: 4
      })
    | (Omit<
        StoredPatchLibrary,
        | 'bankDescriptions'
        | 'eightBit'
        | 'favourites'
        | 'records'
        | 'workspaceBanks'
        | 'version'
        | 'virtualAnalog'
      > & { version: 3 })
    | (Omit<
        StoredPatchLibrary,
        | 'bankDescriptions'
        | 'bankNames'
        | 'eightBit'
        | 'favourites'
        | 'records'
        | 'workspaceBanks'
        | 'version'
        | 'virtualAnalog'
      > & {
        version: 2
      })
    | (Omit<
        StoredPatchLibrary,
        | 'bankDescriptions'
        | 'bankNames'
        | 'effects'
        | 'eightBit'
        | 'favourites'
        | 'records'
        | 'workspaceBanks'
        | 'version'
        | 'virtualAnalog'
      > & { version: 1 })
    | undefined
  try {
    stored = await runTransaction(workspaceStoreName, 'readonly', (store) => store.get(recordKey))
  } catch (error) {
    throw asStorageError(error, 'read-failed', 'The saved workspace could not be read.')
  }

  if (!stored) return null
  if (
    (stored.version !== 1 &&
      stored.version !== 2 &&
      stored.version !== 3 &&
      stored.version !== 4 &&
      stored.version !== 5 &&
      stored.version !== 6 &&
      stored.version !== 7 &&
      stored.version !== 8 &&
      stored.version !== 9) ||
    !Array.isArray(stored.loadedBanks) ||
    typeof stored.voices !== 'object' ||
    ((stored.version === 4 ||
      stored.version === 5 ||
      stored.version === 6 ||
      stored.version === 7 ||
      stored.version === 8 ||
      stored.version === 9) &&
      !Array.isArray(stored.workspaceBanks))
  ) {
    throw new PatchLibraryStorageError(
      'incompatible',
      'The saved patch library is not compatible with this version.',
    )
  }

  try {
    const storedEffects =
      stored.version !== 1 && stored.effects && typeof stored.effects === 'object'
        ? stored.effects
        : {}
    const storedBankNames =
      (stored.version === 3 ||
        stored.version === 4 ||
        stored.version === 5 ||
        stored.version === 6 ||
        stored.version === 7 ||
        stored.version === 8 ||
        stored.version === 9) &&
      stored.bankNames &&
      typeof stored.bankNames === 'object'
        ? stored.bankNames
        : {}
    const storedBankDescriptions =
      (stored.version === 5 ||
        stored.version === 6 ||
        stored.version === 7 ||
        stored.version === 8 ||
        stored.version === 9) &&
      stored.bankDescriptions &&
      typeof stored.bankDescriptions === 'object'
        ? stored.bankDescriptions
        : {}
    const workspaceBanks =
      stored.version === 4 ||
      stored.version === 5 ||
      stored.version === 6 ||
      stored.version === 7 ||
      stored.version === 8 ||
      stored.version === 9
        ? [...new Set(stored.workspaceBanks.filter(isWorkspaceBankId))]
        : [...browserBanks]
    // Favourites arrived in version 6; earlier workspaces have none.
    const favourites =
      stored.version === 6 || stored.version === 7 || stored.version === 8 || stored.version === 9
        ? readFavourites(stored.favourites)
        : []
    // 8-Bit presets arrived in version 9, Virtual Analog presets in version 8, and records in
    // version 7; earlier workspaces have none.
    const storedVirtualAnalog =
      stored.version === 8 || stored.version === 9
        ? readStoredPresetVoices(
            stored.virtualAnalog,
            isFm1VaVirtualAnalogVoice,
            (id) => id in stored.voices,
            'Virtual Analog',
          )
        : {}
    const storedEightBit =
      stored.version === 9
        ? readStoredPresetVoices(
            stored.eightBit,
            isFm1VaEightBitVoice,
            (id) => id in stored.voices || id in storedVirtualAnalog,
            '8-Bit',
          )
        : {}
    const storedRecords =
      stored.version === 7 || stored.version === 8 || stored.version === 9
        ? readStoredRecords(
            stored.records,
            (id) => id in stored.voices || id in storedVirtualAnalog || id in storedEightBit,
          )
        : {}
    if (!favourites) {
      throw new PatchLibraryStorageError(
        'incompatible',
        'The saved patch library contains an unreadable favourite.',
      )
    }
    if (workspaceBanks.length === 0 || workspaceBanks.length > maximumWorkspaceBanks) {
      throw new PatchLibraryStorageError(
        'incompatible',
        'The saved patch library has an invalid workspace bank list.',
      )
    }

    const compacted = compactWorkspaceBanks({
      bankDescriptions: Object.fromEntries(
        Object.entries(storedBankDescriptions)
          .filter(
            ([bank, description]) =>
              workspaceBanks.includes(bank) && typeof description === 'string',
          )
          .map(([bank, description]) => [
            bank,
            description.trim().slice(0, bankDescriptionLength).trimEnd(),
          ])
          .filter(([, description]) => Boolean(description)),
      ),
      bankNames: Object.fromEntries(
        Object.entries(storedBankNames)
          .filter(([bank, name]) => workspaceBanks.includes(bank) && typeof name === 'string')
          .map(([bank, name]) => [bank, name.trim().slice(0, workspaceBankTitleLength).trimEnd()])
          .filter(([, name]) => Boolean(name)),
      ),
      effects: storedEffects,
      eightBit: storedEightBit,
      favourites,
      loadedBanks: stored.loadedBanks.filter((bank) => workspaceBanks.includes(bank)),
      records: storedRecords,
      virtualAnalog: storedVirtualAnalog,
      voices: stored.voices,
      workspaceBanks,
    })
    const voices = normalizeStoredVoices(compacted.voices)
    const effects = Object.fromEntries(
      [
        ...Object.keys(voices),
        ...Object.keys(compacted.virtualAnalog),
        ...Object.keys(compacted.eightBit),
      ].map((id) => [id, normalizeFm1Effects(compacted.effects[id])]),
    )
    return {
      ...compacted,
      effects,
      savedAt: storedRevision(stored) ?? '',
      version: 9 as const,
      voices,
    }
  } catch (error) {
    throw asStorageError(
      error,
      'incompatible',
      'The saved patch library is incompatible or damaged.',
    )
  }
}

/**
 * Saves the workspace over the stored one only if that is still the revision this tab loaded or
 * last wrote, and returns the new revision. Another tab's save, or a newer release's record, in
 * between makes it refuse with a `conflict` error and leave storage unchanged.
 */
export async function saveStoredPatchLibrary(
  library: Omit<StoredPatchLibrary, 'savedAt' | 'version'>,
  expected: StoredWorkspaceRevision,
): Promise<string> {
  let conflict = false
  let writeError: unknown
  let savedAt = new Date().toISOString()
  try {
    await runTransaction(workspaceStoreName, 'readwrite', (store, transaction) => {
      const read = store.get(recordKey)
      read.onsuccess = () => {
        const stored: unknown = read.result
        const current = storedRevision(stored)
        const version =
          stored && typeof stored === 'object' && 'version' in stored ? stored.version : undefined
        if (
          current !== expected ||
          (typeof version === 'number' && version > currentWorkspaceVersion)
        ) {
          conflict = true
          transaction.abort()
          return
        }
        // Each write moves the revision forward, even within one millisecond of the last.
        const previous = Date.parse(current ?? '')
        if (Number.isFinite(previous) && Date.parse(savedAt) <= previous) {
          savedAt = new Date(previous + 1).toISOString()
        }
        try {
          store.put(
            { ...library, savedAt, version: currentWorkspaceVersion } satisfies StoredPatchLibrary,
            recordKey,
          )
        } catch (error) {
          writeError = error
          transaction.abort()
        }
      }
      return read
    })
  } catch (error) {
    if (conflict) {
      throw new PatchLibraryStorageError(
        'conflict',
        'Another tab saved the library after this tab loaded it.',
      )
    }
    throw asStorageError(writeError ?? error, 'write-failed', 'The workspace could not be saved.')
  }
  return savedAt
}

/**
 * Lists every readable saved bank. A damaged record is counted rather than failing the whole list,
 * and it stays in storage unchanged so a later release can still recover it.
 */
export async function listStoredNamedBanks() {
  let records: unknown[]
  try {
    records = await runTransaction<unknown[]>(namedBankStoreName, 'readonly', (store) =>
      store.getAll(),
    )
  } catch (error) {
    throw asStorageError(error, 'read-failed', 'The saved banks could not be read.')
  }

  const banks: NamedBank[] = []
  let damagedCount = 0
  for (const record of records) {
    try {
      validateNamedBank(record)
      // Mask any voice byte above seven bits, as the workspace does, so a bank an earlier release
      // stored unmasked loads, sends, and backs up like any other. The record is a fresh copy.
      for (const slot of record.slots) {
        if ('voice' in slot) slot.voice = normalizeStoredDx7Voice(slot.voice) ?? slot.voice
      }
      banks.push(record)
    } catch {
      damagedCount += 1
    }
  }
  banks.sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))
  return { banks, damagedCount }
}

export async function saveStoredNamedBank(bank: NamedBank) {
  validateNamedBank(bank)
  try {
    return await runTransaction<IDBValidKey>(namedBankStoreName, 'readwrite', (store) =>
      store.put(bank),
    )
  } catch (error) {
    throw asStorageError(error, 'write-failed', 'The bank could not be saved.')
  }
}

/**
 * Adds a saved bank only when no record has its id, so it can never overwrite one, including a
 * damaged record the saved-bank list hides. Returns whether it was added.
 */
export async function addStoredNamedBank(bank: NamedBank) {
  validateNamedBank(bank)
  try {
    await runTransaction<IDBValidKey>(namedBankStoreName, 'readwrite', (store) => store.add(bank))
    return true
  } catch (error) {
    if (error instanceof DOMException && error.name === 'ConstraintError') return false
    throw asStorageError(error, 'write-failed', 'The bank could not be saved.')
  }
}

export async function deleteStoredNamedBank(id: string) {
  if (!id) throw new Error('A saved bank ID is required.')
  try {
    return await runTransaction<undefined>(namedBankStoreName, 'readwrite', (store) =>
      store.delete(id),
    )
  } catch (error) {
    throw asStorageError(error, 'write-failed', 'The bank could not be deleted.')
  }
}
